import { useState } from 'react';
import { App, Button, Drawer, Empty, Input, Popconfirm, Space } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { KeyValueRows } from '@presentation/components/workbench/KeyValueRows';
import { VariableHygienePanel } from '@presentation/components/workbench/VariableHygienePanel';
import {
  useActivateEnvironment,
  useCreateEnvironment,
  useDeleteEnvironment,
  useEnvironments,
  useUpdateEnvironment,
} from '@presentation/hooks/useEnvironments';
import { useGlobalVariables, useSyncGlobalVariables } from '@presentation/hooks/useGlobalVariables';
import type { Variable } from '@core/entities/Variable';

interface EnvironmentManagerDrawerProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

const emptyVariable = (): Variable => ({
  id: crypto.randomUUID(),
  key: '',
  value: '',
  type: 'default',
  isEnabled: true,
  position: 0,
});

export const EnvironmentManagerDrawer = ({
  workspaceId,
  open,
  onClose,
}: EnvironmentManagerDrawerProps) => {
  const { message } = App.useApp();
  const { data: environments } = useEnvironments(workspaceId);
  const { data: globals } = useGlobalVariables(workspaceId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rows, setRows] = useState<Variable[]>([]);
  const [globalRows, setGlobalRows] = useState<Variable[]>([]);
  const [newName, setNewName] = useState('');
  // Tracks which environment's variables are currently loaded into `rows`, so
  // a background refetch of `environments` (same env, unchanged id) doesn't
  // clobber in-progress edits — only an actual selection change re-syncs.
  const [loadedEnvId, setLoadedEnvId] = useState<string | null>(null);
  const [globalsLoaded, setGlobalsLoaded] = useState(false);

  const createEnvironment = useCreateEnvironment(workspaceId);
  const updateEnvironment = useUpdateEnvironment(selectedId ?? '', workspaceId);
  const activateEnvironment = useActivateEnvironment(workspaceId);
  const deleteEnvironment = useDeleteEnvironment(workspaceId);
  const syncGlobals = useSyncGlobalVariables(workspaceId);

  const selected = environments?.find((environment) => environment.id === selectedId);

  if (selected && selected.id !== loadedEnvId) {
    setLoadedEnvId(selected.id);
    setRows(selected.variables);
  }

  if (globals && !globalsLoaded) {
    setGlobalsLoaded(true);
    setGlobalRows(globals);
  }

  return (
    <Drawer title="Environments &amp; Globals" open={open} onClose={onClose} size={720}>
      <div className="flex h-full gap-4">
        <div className="w-52 shrink-0 border-r border-black/10 pr-3 dark:border-white/10">
          <Space.Compact className="mb-2 w-full">
            <Input
              size="small"
              placeholder="New environment"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onPressEnter={() => {
                if (!newName.trim()) return;
                createEnvironment.mutate(
                  { name: newName.trim() },
                  {
                    onSuccess: (env) => {
                      setSelectedId(env.id);
                      setNewName('');
                    },
                  },
                );
              }}
            />
            <Button
              size="small"
              icon={<PlusOutlined />}
              loading={createEnvironment.isPending}
              onClick={() => {
                if (!newName.trim()) return;
                createEnvironment.mutate(
                  { name: newName.trim() },
                  {
                    onSuccess: (env) => {
                      setSelectedId(env.id);
                      setNewName('');
                    },
                  },
                );
              }}
            />
          </Space.Compact>

          <div className="flex flex-col">
            {[
              { id: '__globals__', name: 'Globals', isActive: false },
              { id: '__hygiene__', name: 'Hygiene report', isActive: false },
              ...(environments ?? []),
            ].map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(item.id)}
                className={`flex cursor-pointer items-center gap-1 rounded px-2 py-1.5 ${selectedId === item.id ? 'bg-black/5 dark:bg-white/10' : ''}`}
              >
                <span className="flex-1 truncate text-sm">{item.name}</span>
                {'isActive' in item && item.isActive && (
                  <span className="text-[10px] text-emerald-500">Active</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          {selectedId === '__globals__' && (
            <div className="flex flex-col gap-3">
              <h4 className="text-sm font-medium">Global variables</h4>
              <KeyValueRows rows={globalRows} onChange={setGlobalRows} createRow={emptyVariable} />
              <Button
                type="primary"
                size="small"
                loading={syncGlobals.isPending}
                className="self-start"
                onClick={() =>
                  syncGlobals.mutate(globalRows, {
                    onSuccess: () => void message.success('Globals saved'),
                  })
                }
              >
                Save globals
              </Button>
            </div>
          )}

          {selected && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">{selected.name}</h4>
                <Space>
                  {!selected.isActive && (
                    <Button size="small" onClick={() => activateEnvironment.mutate(selected.id)}>
                      Set active
                    </Button>
                  )}
                  <Popconfirm
                    title="Delete this environment?"
                    onConfirm={() =>
                      deleteEnvironment.mutate(selected.id, {
                        onSuccess: () => setSelectedId(null),
                      })
                    }
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              </div>
              <KeyValueRows
                rows={rows}
                onChange={setRows}
                typeOptions={[
                  { label: 'Default', value: 'default' },
                  { label: 'Secret', value: 'secret' },
                ]}
                createRow={emptyVariable}
              />
              <Button
                type="primary"
                size="small"
                className="self-start"
                loading={updateEnvironment.isPending}
                onClick={() =>
                  updateEnvironment.mutate(
                    { name: selected.name, variables: rows },
                    { onSuccess: () => void message.success('Environment saved') },
                  )
                }
              >
                Save
              </Button>
            </div>
          )}

          {selectedId === '__hygiene__' && <VariableHygienePanel workspaceId={workspaceId} />}

          {!selected && selectedId !== '__globals__' && selectedId !== '__hygiene__' && (
            <Empty description="Select or create an environment" />
          )}
        </div>
      </div>
    </Drawer>
  );
};
